/** Riverr popup store admin — URL prefix and helpers (sidebar swap + routing). */
// Canonical store admin prefix. /shop/settings/:id/* keeps working for old
// links and bookmarks; the sidebar and new links use /stores/:id/*.
export const POPUP_STORE_SETTINGS_PREFIX = "/stores";
export const LEGACY_POPUP_STORE_SETTINGS_PREFIX = "/shop/settings";

const STORE_ADMIN_PATH_RE = /^\/(?:stores|shop\/settings)\/([^/]+)/;

export function isPopupStoreAdminPath(pathname) {
  return STORE_ADMIN_PATH_RE.test(pathname || "");
}

export function parsePopupStoreIdFromPath(pathname) {
  const m = (pathname || "").match(STORE_ADMIN_PATH_RE);
  return m ? m[1] : null;
}

/** MyCart / Riverr popup stores (platform 13) — not Shopify/Etsy/etc. */
export function isPopupStoreShop(shop = {}) {
  return (
    String(shop?.platformId || "").trim() === "13" ||
    String(shop?.platformName || "")
      .trim()
      .toLowerCase() === "popup store"
  );
}

/**
 * Whether /shop/settings/:id/* should swap in the popup-store sidebar.
 * Shopify (and other connected channels) can open Branding without the full
 * Store Setup chrome — they have no store-settings IA of their own.
 */
export function shouldUsePopupStoreSidebar(pathname, shop) {
  if (!isPopupStoreAdminPath(pathname)) return false;
  if (shop) return isPopupStoreShop(shop);
  // Shop list not loaded yet: branding-only deep links keep the main nav;
  // other settings paths still assume MyCart popup chrome.
  return !/^\/(?:stores|shop\/settings)\/[^/]+\/branding(\/|$)/.test(
    pathname || ""
  );
}

export const RIVER_LAST_NON_STORE_PATH_KEY = "riverr_last_non_store_path";

/** Routes that only redirect into popup store admin — never use as "back" targets. */
const POPUP_STORE_REDIRECT_HOP_PATHS = new Set([
  "/analytics/storefront",
  "/analytics-screen",
]);

export function isPopupStoreRedirectHopPath(pathname) {
  const path = String(pathname || "").split(/[?#]/)[0];
  return POPUP_STORE_REDIRECT_HOP_PATHS.has(path);
}

/**
 * True when `pathname` is safe to remember as the last non-store destination.
 */
export function isValidPopupStoreExitPath(pathname) {
  const path = String(pathname || "").split(/[?#]/)[0];
  if (!path || path === "/") return false;
  if (isPopupStoreAdminPath(path)) return false;
  if (isPopupStoreRedirectHopPath(path)) return false;
  return true;
}

/**
 * Whether a shop is a "Company Store" — the enterprise-admin-only variant
 * of a MyCart shop that exposes Channel & quotes settings, Feature badges,
 * and (over time) other admin-grade features. Defaults to false when the
 * field is absent so existing popup shops keep their current UI surface.
 */
export function isCompanyStore(shop) {
  return shop?.storeType === "company";
}

export function popupStorePathFor(shopId, segment) {
  return `${POPUP_STORE_SETTINGS_PREFIX}/${shopId}/${segment}`;
}

function isPathActive(pathname, target) {
  if (!target) return false;
  return pathname === target || pathname.startsWith(`${target}/`);
}

/**
 * IA for the popup store admin sidebar.
 *
 * Top-level order: Products, Collections, Analytics and Opportunities as
 * standalone leaves, then Store Setup / Online Store as expandable parents. Visibility
 * flags are applied at build time so empty parents are omitted.
 */
export function buildPopupStoreNav({
  shopId,
  showDomains,
  showEmails,
  showOpportunities,
  showCompanyOnly,
  customizeHref,
  showAnalytics,
  showBranding,
  showEmailMarketing,
  showCustomerSupport,
}) {
  const base = `${POPUP_STORE_SETTINGS_PREFIX}/${shopId}`;
  const items = [];

  // Products and Collections are the daily work, so they lead the nav as
  // top level items. Copy store moved to the store switcher menu.
  items.push(
    { id: "products", label: "Products", type: "leaf", to: `${base}/products` },
    {
      id: "collections",
      label: "Collections",
      type: "leaf",
      to: `${base}/collections`,
    }
  );

  if (showAnalytics) {
    items.push({
      id: "analytics",
      label: "Analytics",
      type: "leaf",
      to: `${base}/analytics`,
    });
  }

  if (showOpportunities) {
    items.push({
      id: "opportunities",
      label: "Opportunities",
      type: "leaf",
      to: `${base}/opportunities`,
    });
  }

  const storeSetupChildren = [
    { id: "info", label: "Store info", to: `${base}/info` },
    ...(showDomains
      ? [{ id: "domains", label: "Domains", to: `${base}/domains` }]
      : []),
    ...(showEmails
      ? [{ id: "emails", label: "Emails", to: `${base}/emails` }]
      : []),
    ...(showCustomerSupport
      ? [
          {
            id: "customer-support",
            label: "Customer support",
            to: `${base}/customer-support`,
          },
        ]
      : []),
    ...(showEmailMarketing
      ? [
          {
            id: "email-marketing",
            label: "Email marketing",
            to: `${base}/email-marketing`,
          },
        ]
      : []),
    ...(showBranding
      ? [{ id: "branding", label: "Branding", to: `${base}/branding` }]
      : []),
    {
      id: "analytics-tracking",
      label: "Analytics & tracking",
      to: `${base}/analytics-tracking`,
    },
    { id: "shipping", label: "Shipping", to: `${base}/shipping` },
    { id: "access", label: "Store access", to: `${base}/access` },
    { id: "coupons", label: "Coupons", to: `${base}/coupons` },
    { id: "announcement", label: "Announcement bar", to: `${base}/announcement` },
    { id: "campaign", label: "Campaign", to: `${base}/campaign` },
    ...(showCompanyOnly
      ? [
          {
            id: "channel-settings",
            label: "Channel & quotes",
            to: `${base}/channel-settings`,
          },
        ]
      : []),
  ];

  if (storeSetupChildren.length) {
    items.push({
      id: "store-setup",
      label: "Store Setup",
      type: "parent",
      children: storeSetupChildren,
    });
  }

  const onlineStoreChildren = [
    { id: "themes", label: "Themes", to: `${base}/themes` },
    ...(customizeHref
      ? [
          {
            id: "customize",
            label: "Customize",
            href: customizeHref,
            external: true,
          },
        ]
      : []),
    { id: "pages", label: "Pages", to: `${base}/pages` },
    {
      id: "product-display",
      label: "Product display",
      to: `${base}/product-display`,
    },
    { id: "migrate", label: "Migrate store", to: `${base}/migrate` },
    ...(showCompanyOnly
      ? [
          {
            id: "feature-badges",
            label: "Feature badges",
            to: `${base}/feature-badges`,
          },
        ]
      : []),
  ];

  if (onlineStoreChildren.length) {
    items.push({
      id: "online-store",
      label: "Online Store",
      type: "parent",
      children: onlineStoreChildren,
    });
  }

  return items;
}

export function findActivePopupStoreParent(pathname, navItems = []) {
  for (const item of navItems) {
    if (item.type !== "parent") continue;
    const hit = item.children.some(
      (child) => !child.external && isPathActive(pathname, child.to)
    );
    if (hit) return item.id;
  }
  return null;
}

export function isPopupStoreNavItemActive(pathname, item, { exact = false } = {}) {
  if (item.external || !item.to) return false;
  if (exact) return pathname === item.to;
  return isPathActive(pathname, item.to);
}
